import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import { HiMinus, HiPlus, HiRefresh } from 'react-icons/hi';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const ChartEngine1 = ({ DataSource }) => {

    const [brushData, setBrushData] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

    const FetchDataTMP = DataSource.filter((item) => (item.DisplayNo === 2));
    const FetchDataRPM = DataSource.filter((item) => (item.DisplayNo === 3));

    // Memoized data processing for better performance
    const data = useMemo(() => {
        // Get all unique dates from both temperature and RPM data
        const allDates = new Set([
            ...FetchDataTMP.map(item => item.RefDate),
            ...FetchDataRPM.map(item => item.RefDate)
        ]);

        // Convert to array and sort by date
        const sortedDates = Array.from(allDates).sort((a, b) => dayjs.utc(a).diff(dayjs.utc(b)));

        return sortedDates.map(refDate => {
            const tmpItem = FetchDataTMP.find(item => item.RefDate === refDate);
            const rpmItem = FetchDataRPM.find(item => item.RefDate === refDate);

            return {
                refDateTime: dayjs.utc(refDate).format('YYYY-MM-DD HH:mm:ss'),
                Temperatur: tmpItem ? tmpItem.Value1 : null,
                Rpm: rpmItem ? rpmItem.Value1 : null
            };
        });
    }, [FetchDataTMP, FetchDataRPM]);

    // Calculate display data based on zoom
    const displayData = useMemo(() => {
        if (brushData) {
            return data.slice(brushData.startIndex, brushData.endIndex + 1);
        }

        if (zoomLevel > 1) {
            const totalPoints = data.length;
            const visiblePoints = Math.floor(totalPoints / zoomLevel);
            const startIndex = Math.floor((zoomRange.start / 100) * (totalPoints - visiblePoints));
            const endIndex = startIndex + visiblePoints;
            return data.slice(startIndex, endIndex);
        }

        return data;
    }, [data, brushData, zoomLevel, zoomRange]);

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 5));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 1));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setZoomRange({ start: 0, end: 100 });
        setBrushData(null);
    };

    const handleZoomRangeChange = (start, end) => {
        setZoomRange({ start, end });
    };

    const lineColors = ['#8884d8', '#1CA449'];

    return (
        <div className="p-4 flex flex-col gap-4 w-full">
            <div className="flex gap-2 justify-end">
                <button
                    onClick={handleZoomOut}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom Out"
                    disabled={zoomLevel <= 1}
                >
                    <HiMinus className="w-4 h-4" />
                </button>

                <span className="px-3 py-1 bg-gray-700 text-white rounded text-sm font-medium">
                    {Math.round(zoomLevel * 100)}%
                </span>

                <button
                    onClick={handleZoomIn}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom In"
                    disabled={zoomLevel >= 5}
                >
                    <HiPlus className="w-4 h-4" />
                </button>

                <button
                    onClick={handleResetZoom}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    title="Reset Zoom"
                >
                    <HiRefresh className="w-4 h-4" />
                </button>
            </div>

            <div className="flex justify-evenly lg:flex-row items-center gap-12 flex-col relative">
                <ResponsiveContainer width="100%" height={window.innerWidth > 768 ? 400 : 250}>
                    <LineChart data={displayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                            dataKey="refDateTime"
                            tickFormatter={(value) => dayjs.utc(value).format('HH:mm:ss')}
                            stroke="#FFFFFF"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            label={{ position: "bottom", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis
                            stroke="#374151"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            domain={['auto', 'auto']}
                            label={{ angle: -90, position: "left", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            width={80}
                            name={(() => {
                                const hasTemp = FetchDataTMP.length > 0;
                                const hasRPM = FetchDataRPM.length > 0;

                                if (hasTemp && hasRPM) {
                                    return "Temperature (°C) / RPM";
                                } else if (hasRPM) {
                                    return "RPM";
                                } else if (hasTemp) {
                                    return "Temperature (°C)";
                                }
                                return "Value";
                            })()}
                            tickFormatter={(value) => {
                                // Check if we have both temperature and RPM data
                                const hasTemp = FetchDataTMP.length > 0;
                                const hasRPM = FetchDataRPM.length > 0;

                                if (hasTemp && hasRPM) {
                                    return `${value}`;
                                } else if (hasRPM) {
                                    return `${value} RPM`;
                                } else if (hasTemp) {
                                    return `${value} °C`;
                                }
                                return value;
                            }}
                        />
                        <Tooltip
                            labelFormatter={(value) => {
                                const dataIndex = displayData.findIndex(item => item.refDateTime === value);
                                return dataIndex !== -1 ? displayData[dataIndex].refDateTime : '';
                            }}
                            formatter={(value, name) => {
                                if (name === 'Temperatur') {
                                    return [value + ' °C', 'Temperature'];
                                } else if (name === 'Rpm') {
                                    return [value + ' RPM', 'RPM'];
                                }
                                return [value, name];
                            }}
                            labelStyle={{ color: "#FFFFFF" }}
                            itemStyle={{ color: "#FFFFFF" }}
                            contentStyle={{
                                backgroundColor: "#1e2939",
                                borderColor: "#4B5563",
                                borderRadius: "8px",
                                border: "1px solid #4B5563"
                            }}
                        />
                        {(FetchDataTMP.length > 0 || FetchDataRPM.length > 0) && (
                            <Legend
                                wrapperStyle={{ color: "#FFFFFF" }}
                                iconType="line"
                                formatter={(value) => {
                                    if (value === 'Temperatur') return 'Temperature (°C)';
                                    if (value === 'Rpm') return 'RPM';
                                    return value;
                                }}
                            />
                        )}
                        {FetchDataTMP.length > 0 && (
                            <Line
                                type="monotone"
                                dataKey="Temperatur"
                                stroke={lineColors[0]}
                                strokeWidth={zoomLevel > 2 ? 3 : 2}
                                dot={{
                                    fill: lineColors[0],
                                    strokeWidth: 2,
                                    r: zoomLevel > 2 ? 4 : 3,
                                    display: zoomLevel > 3 ? 'block' : 'none'
                                }}
                                activeDot={{
                                    r: zoomLevel > 2 ? 6 : 5,
                                    stroke: "#374151",
                                    strokeWidth: 1.5
                                }}
                                connectNulls={false}
                                animationDuration={300}
                            />
                        )}
                        {FetchDataRPM.length > 0 && (
                            <Line
                                type="monotone"
                                dataKey="Rpm"
                                stroke={lineColors[1]}
                                strokeWidth={zoomLevel > 2 ? 3 : 2}
                                dot={{
                                    fill: lineColors[1],
                                    strokeWidth: 2,
                                    r: zoomLevel > 2 ? 4 : 3,
                                    display: zoomLevel > 3 ? 'block' : 'none'
                                }}
                                activeDot={{
                                    r: zoomLevel > 2 ? 6 : 5,
                                    stroke: "#374151",
                                    strokeWidth: 1.5
                                }}
                                connectNulls={false}
                                animationDuration={300}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Zoom Range Slider (optional) */}
            {zoomLevel > 1 && (
                <div className="flex items-center gap-4 mt-4">
                    <span className="text-white text-sm">Zoom Range:</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={zoomRange.start}
                        onChange={(e) => handleZoomRangeChange(parseInt(e.target.value), zoomRange.end)}
                        className="flex-1 h-2 bg-gray-400 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-white text-sm">{zoomRange.start}% - {zoomRange.end}%</span>
                </div>
            )}
        </div>
    )
}

export default ChartEngine1;
